import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, ArrowLeft, Loader2 } from 'lucide-react';
import { ContactFormData } from '@/types';

interface ProjectWizardProps {
  onSubmit: (data: ContactFormData) => Promise<void>;
  isSubmitting: boolean;
}

type Intent = 'Start a Project' | 'Partnership' | 'Just Saying Hi';

/**
 * Multi-step interactive contact form "Wizard".
 * Features differentiated flows depending on the user's intent:
 *   - "Start a Project" → project type → budget → details (4 steps)
 *   - "Partnership" → partnership type → details (3 steps)
 *   - "Just Saying Hi" → details only (2 steps)
 */
export const ProjectWizard: React.FC<ProjectWizardProps> = ({ onSubmit, isSubmitting }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    projectType: 'Web App / SaaS Platform',
    budgetRange: '£5k - £15k',
    details: '',
  });

  // Step 1: Intent
  const intents: Intent[] = ['Start a Project', 'Partnership', 'Just Saying Hi'];
  const [intent, setIntent] = useState<Intent>('Start a Project');

  // Step 2 options per intent
  const projectTypes = ['Web App / SaaS Platform', 'Website / E-Commerce', 'Internal Tool / Dashboard', 'AI & Automation', 'Mobile / Cross-Platform', 'Other'];
  const partnershipTypes = ['Referral Partner', 'Agency / White-Label', 'Technology Partner', 'Other'];

  // Step 3 options (project path only)
  const budgets = ['< £5k', '£5k - £15k', '£15k - £50k', '£50k+'];

  /**
   * Returns the total number of steps for the current intent.
   */
  const getTotalSteps = (): number => {
    switch (intent) {
      case 'Start a Project': return 4; // intent → type → budget → details
      case 'Partnership': return 3; // intent → partnership type → details
      case 'Just Saying Hi': return 2; // intent → details
    }
  };

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, projectType: intent === 'Just Saying Hi' ? 'General Inquiry' : formData.projectType });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (step < getTotalSteps()) {
        e.preventDefault();
        nextStep();
      }
    }
  };

  const updateField = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Returns the label/placeholder text for the details textarea based on intent.
   */
  const getDetailsPlaceholder = (): string => {
    switch (intent) {
      case 'Start a Project': return 'Tell us a bit about your goals...';
      case 'Partnership': return 'Tell us about your partnership idea...';
      case 'Just Saying Hi': return "What's on your mind?";
    }
  };

  /**
   * Returns the label for the details field based on intent.
   */
  const getDetailsLabel = (): string => {
    switch (intent) {
      case 'Start a Project': return 'Project Details';
      case 'Partnership': return 'Partnership Details';
      case 'Just Saying Hi': return 'Message';
    }
  };

  /**
   * Determines whether the current step is the final details form.
   */
  const isDetailsStep = (): boolean => step === getTotalSteps();

  /**
   * Renders the shared contact details form (last step for every path).
   */
  const renderDetailsStep = () => (
    <div className="space-y-6">
      <h2 className="text-3xl font-display font-medium text-white mb-2">
        {intent === 'Just Saying Hi' ? "We'd love to hear from you." : 'Last few details.'}
      </h2>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Name</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg p-4 text-white focus:outline-none focus:border-sapphire-500 transition-colors"
            placeholder="Jane Doe"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Email</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg p-4 text-white focus:outline-none focus:border-sapphire-500 transition-colors"
            placeholder="jane@company.com"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{getDetailsLabel()}</label>
          <textarea
            required
            value={formData.details}
            onChange={(e) => updateField('details', e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg p-4 text-white focus:outline-none focus:border-sapphire-500 transition-colors min-h-[120px]"
            placeholder={getDetailsPlaceholder()}
            minLength={10}
          />
        </div>
      </div>
    </div>
  );

  /**
   * Renders a selection grid of options.
   */
  const renderOptionGrid = (
    title: string,
    options: string[],
    selectedValue: string,
    onSelect: (value: string) => void,
    columns: 1 | 2 = 1
  ) => (
    <div className="space-y-6">
      <h2 className="text-3xl font-display font-medium text-white mb-2">{title}</h2>
      <div className={`grid grid-cols-1 ${columns === 2 ? 'sm:grid-cols-2' : ''} gap-3`}>
        {options.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onSelect(item)}
            className={`p-${columns === 2 ? '4' : '6'} text-left rounded-xl border transition-all duration-300 group flex items-center justify-between ${selectedValue === item
              ? 'bg-sapphire-500/10 border-sapphire-500 text-white'
              : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30 hover:text-white'
              }`}
          >
            <span className={`${columns === 2 ? 'text-base' : 'text-lg'} font-medium`}>{item}</span>
            <ArrowRight className={`w-5 h-5 transition-transform ${selectedValue === item ? 'text-sapphire-500 translate-x-0' : 'opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0'}`} />
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep = () => {
    // ── Step 1: Intent (shared by all paths) ──
    if (step === 1) {
      return renderOptionGrid(
        'What can we do for you?',
        intents,
        intent,
        (item) => {
          const selectedIntent = item as Intent;
          setIntent(selectedIntent);
          if (selectedIntent !== 'Start a Project') {
            updateField('budgetRange', 'N/A');
          }
          if (selectedIntent === 'Just Saying Hi') {
            updateField('projectType', 'General Inquiry');
          }
          nextStep();
        }
      );
    }

    // ── "Start a Project" path ──
    if (intent === 'Start a Project') {
      if (step === 2) {
        return renderOptionGrid(
          'What are you building?',
          projectTypes,
          formData.projectType,
          (type) => { updateField('projectType', type); nextStep(); },
          2
        );
      }
      if (step === 3) {
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-display font-medium text-white mb-2">Do you have a budget in mind?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {budgets.map((range) => (
                <button
                  key={range}
                  type="button"
                  onClick={() => { updateField('budgetRange', range); nextStep(); }}
                  className={`p-4 text-left rounded-xl border transition-all duration-200 ${formData.budgetRange === range
                    ? 'bg-sapphire-500/10 border-sapphire-500 text-white'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30 hover:text-white'
                    }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={nextStep}
              className="text-sm text-gray-500 hover:text-white underline decoration-gray-700 underline-offset-4 mt-4"
            >
              Skip for now
            </button>
          </div>
        );
      }
      if (step === 4) return renderDetailsStep();
    }

    // ── "Partnership" path ──
    if (intent === 'Partnership') {
      if (step === 2) {
        return renderOptionGrid(
          'What kind of partnership?',
          partnershipTypes,
          formData.projectType,
          (type) => { updateField('projectType', `Partnership: ${type}`); nextStep(); },
          2
        );
      }
      if (step === 3) return renderDetailsStep();
    }

    // ── "Just Saying Hi" path ──
    if (intent === 'Just Saying Hi') {
      if (step === 2) return renderDetailsStep();
    }

    return null;
  };

  const totalSteps = getTotalSteps();

  return (
    <div className="w-full max-w-xl mx-auto" onKeyDown={handleKeyDown}>
      {/* Progress Bar */}
      <div className="flex gap-2 mb-8">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full transition-colors duration-500 ${s <= step ? 'bg-sapphire-500' : 'bg-white/10'}`}
          />
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        <AnimatePresence mode="wait">
          <motion.div
            key={`${intent}-${step}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6">
          {step > 1 ? (
            <button
              type="button"
              onClick={prevStep}
              className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <div />
          )}

          {isDetailsStep() ? (
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-sapphire-500 hover:bg-sapphire-600 text-white px-8 py-3 rounded-full font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {intent === 'Just Saying Hi' ? 'Send Message' : 'Send Proposal'}
            </button>
          ) : null}
        </div>
      </form>

      {/* Honeypot */}
      <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off"
        onChange={(e) => updateField('_gotcha' as keyof ContactFormData, e.target.value)}
      />
    </div>
  );
};
